import type { ConsultationFeeEnsureResult } from '@/dashboard/api';

/** Small adapter for the existing explicit Save/Finish acts. It never runs a
 * writer from a read, navigation join, render or finalized clinical addition. */
export interface ConsultationFeeSaveContext {
  readonly appointment: { readonly id: number; readonly clinicId: number };
  readonly patient: { readonly patientId: string | null };
  readonly caseSheet: { readonly id: number; readonly appointmentId: number; readonly patientId: string | null; readonly status: string } | null;
  readonly consultation?: { readonly kind: 'unavailable' } | {
    readonly kind: 'ready'; readonly contextKey: string;
    readonly bill: { readonly id: number; readonly status: string; readonly feeRecorded: boolean } | null;
    readonly hints: { readonly canRecordFee?: boolean };
    readonly view: { readonly financialSource: string; readonly financials: unknown };
  };
}

interface Attempt {
  readonly requestId: string;
  readonly bearer: string;
  receipt?: ConsultationFeeEnsureResult;
  running: boolean;
}

export function createConsultationFeeSave() {
  // A route parameter change does not erase a submitted operation. On return
  // to that same actor/visit, resume its exact request and read fresh truth.
  const attempts = new Map<string, Attempt>();
  return async function save<T extends ConsultationFeeSaveContext>(input: {
    readonly identity: string | undefined;
    readonly context: T;
    readonly current: () => boolean;
    readonly bearer: () => Promise<string | null>;
    readonly requestId: () => string;
    readonly submit: (appointmentId: number, requestId: string, bearer: string) => Promise<ConsultationFeeEnsureResult>;
    readonly read: (appointmentId: number, bearer: string) => Promise<T>;
    readonly apply: (context: T) => void;
  }): Promise<boolean> {
    const { context, current } = input;
    const sheet = context.caseSheet;
    if (!current()) return false;
    if (sheet?.status !== 'draft') return true;
    const fee = context.consultation;
    const key = JSON.stringify([input.identity, context.appointment.id, context.appointment.clinicId, context.patient.patientId, sheet.id]);
    let attempt = attempts.get(key);
    if (!attempt) {
      // Unavailable policy/config remains unknown; this does not invent a new
      // prerequisite for clinical care. A saved fee is never re-defaulted.
      if (fee?.kind !== 'ready' || fee.bill?.feeRecorded || fee.hints.canRecordFee !== true ||
          fee.view.financialSource !== 'eligibility' || fee.view.financials === null ||
          (fee.bill !== null && fee.bill.status !== 'draft')) return true;
      if (!input.identity || context.patient.patientId === null || sheet.appointmentId !== context.appointment.id ||
          sheet.patientId !== context.patient.patientId) return false;
      const bearer = await input.bearer();
      if (!current() || !bearer) return false;
      // A competing explicit act may have entered while session lookup waited.
      attempt = attempts.get(key);
      if (!attempt) {
        attempt = { requestId: input.requestId(), bearer, running: false };
        attempts.set(key, attempt);
      }
    }
    if (attempt.running) return false;
    attempt.running = true;
    try {
      if (await input.bearer() !== attempt.bearer || !current()) return false;
      if (!attempt.receipt) {
        const receipt = await input.submit(context.appointment.id, attempt.requestId, attempt.bearer);
        if (receipt?.requestId !== attempt.requestId || receipt.appointmentId !== context.appointment.id ||
            !Number.isSafeInteger(receipt.billId) || receipt.billId <= 0 ||
            !Number.isSafeInteger(receipt.itemId) || receipt.itemId <= 0 || typeof receipt.created !== 'boolean') return false;
        attempt.receipt = receipt;
      }
      // The old request can commit after a visit switch. Keep its identity,
      // but never read or install its financial truth into the next visit.
      if (!current() || await input.bearer() !== attempt.bearer) return false;
      const fresh = await input.read(context.appointment.id, attempt.bearer);
      const next = fresh.consultation;
      const readerIdentity = (value: string) => value.slice(0, value.lastIndexOf(':'));
      if (!current() || await input.bearer() !== attempt.bearer ||
          fresh.appointment.id !== context.appointment.id || fresh.appointment.clinicId !== context.appointment.clinicId ||
          fresh.patient.patientId !== context.patient.patientId || fresh.caseSheet?.id !== sheet.id ||
          fresh.caseSheet.appointmentId !== context.appointment.id || fresh.caseSheet.patientId !== sheet.patientId ||
          fresh.caseSheet.status !== 'draft' || fee?.kind !== 'ready' || next?.kind !== 'ready' ||
          readerIdentity(next.contextKey) !== readerIdentity(fee.contextKey) ||
          next.bill?.id !== attempt.receipt.billId || !next.bill.feeRecorded ||
          next.view.financialSource !== 'saved_line' || next.view.financials === null) return false;
      // A concurrent legitimate waiver/discount wins. Only the authoritative
      // fresh read is installed; no amount from the operation receipt exists.
      input.apply(fresh);
      attempts.delete(key);
      return true;
    } finally {
      attempt.running = false;
    }
  };
}
